import * as BABYLON from 'babylonjs';
import PointCamera from './PointCamera';
import PointEvents from '../Enumerations/ViewerEnums';
import PointConstants from '../constants/ViewerConstants';
import Compass from '../components/Compass';
import MeasurementManager from './MeasurementManager';
import BoxManager from './BoxManager';
import PointCloudManager from './PointCloudManager';
import GLBManager from './GLBManager';
declare const InstallTrigger: any; // for FireFox detection

interface PointViewerOptions {
    measurementColor?: string
}

class PointViewer {

    public static readonly MinZoomLevel = 0.2;
    public static readonly DefaultZoomLevel = 1.0;
    public static readonly MaxZoomLevel = 1.5;
    

    public events: EventTarget;

    private _scene: BABYLON.Scene;
    private _engine: BABYLON.Engine;
    private _canvas: HTMLCanvasElement;

    private _divContainer: HTMLDivElement;

    private _compass: Compass;

    private _measurementManager: MeasurementManager;
    private _assemblyManager: GLBManager;
    private _boxManager: BoxManager;

    private _displayMode: DisplayMode;
    private _measurementType: MeasurementType;
    private _lastCameraDegrees: number;
    private _allowZoom: boolean;

    private _options: PointViewerOptions;

    private _pointCloudManager: PointCloudManager;
    public get camera() { return <PointCamera>this._scene?.cameras[0]; }
    public get assemblyManager() { return this._assemblyManager; }
    public get scene() { return this._scene; }
    public get boxManager() { return this._boxManager; }

    public get cameraDirectionDegrees() { return this._lastCameraDegrees; }
    public get cameraFov() { return this.camera.fov }
    public get cameraAlpha() { return this.camera.alpha; }
    public get cameraBeta() { return this.camera.beta; }
    public get displayMode() { return this._displayMode; }
    public get measurementType() { return this._measurementType; }
    public get cameraDirection() {

        return this.camera.getWorldDirection();
    }
    public get pointCloudManager() { return this._pointCloudManager; }
    constructor(div: HTMLDivElement, position : IVector3D, imageDirection: BABYLON.Vector3, pointCloudManager: PointCloudManager, options?: PointViewerOptions) {

        this.events = new EventTarget();

        this._allowZoom = true;
        this._pointCloudManager = pointCloudManager;
        this._divContainer = div;

        this._options = {
            measurementColor: options?.measurementColor ?? MeasurementManager.DefaultValidColor
        }

        this._initializeBabylon(position, imageDirection);

        // WHY IS Z AND Y SWAPPED HERE?
        //const imageDirectionVector = new BABYLON.Vector3(imageDirection.X, imageDirection.Z, imageDirection.Y);
        //this.alignCamera(imageDirectionVector);
        this._resizeCanvas();

        this._displayMode = DisplayMode.Navigation;

        this._compass = new Compass();
        this._compass.createCompass(this._divContainer);

        this._boxManager = new BoxManager(this);

        this.camera.events.addEventListener(PointEvents.Camera.CAMERA_CHANGED, this._handleCameraChange);
        this._handleCameraChange();

        this._measurementManager = new MeasurementManager(this, this._options.measurementColor);
        this._assemblyManager = new GLBManager(this, this.scene);
    }

    public dispose() {
        this._removeEventListeners();

        this._measurementManager?.dispose();
        this._measurementManager = null;

        this._boxManager?.dispose();
        this._boxManager = null;

        this._assemblyManager.dispose();

        this._scene.dispose();
        this._engine.dispose();

        this._scene = null;
        this._engine = null;
    }

    public alignCamera(vector: BABYLON.Vector3) {

        if (vector == null)
            vector = new BABYLON.Vector3(0, 0, 1)

        var cam = this._scene.activeCamera as any;

        if (cam._imageRotation != 0) {
            var m = new BABYLON.Matrix();
            var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam._imageRotation);
            rotQuat.toRotationMatrix(m);
            vector = BABYLON.Vector3.TransformCoordinates(vector, m);
        }

        var currentTarget = <BABYLON.Vector3>cam.target;
        var newPos = currentTarget.subtract(vector.scale(cam.radius));

        cam._currentTarget = (currentTarget);
        cam.setPosition(newPos);

        cam._computeViewMatrix();
        cam._getViewMatrix();
    }

    public changeMeasurementColor(newColor: string) {
        if (this._options.measurementColor != newColor) {
            this._options.measurementColor = newColor;
            this._measurementManager?.setMeasurementColor(newColor);
        }
    }

    public setMeasurements(measurements: any) {
        this._measurementManager?.setMeasurements(measurements);
    }

    public toggleCompass(toggle: boolean) {
        this._compass.toggleCompass(toggle);
    }

    public async changeCameraDirection(imageDirection: BABYLON.Vector3, fov?: number, animate?: boolean) {

        if (imageDirection.length() == 0)
            return;

        // Calculate the new alpha (angle in the horizontal plane)
        let directionToTarget = imageDirection.normalizeToNew();
        let newAlpha = Math.atan2(directionToTarget.x, directionToTarget.z);
        newAlpha = -1*Math.PI /2  - newAlpha;

        // Calculate the new beta (angle in the vertical plane)
        const directionGroundProjection = Math.sqrt(imageDirection.x * imageDirection.x + imageDirection.z * imageDirection.z);
        let newBeta = Math.atan2(directionGroundProjection, imageDirection.y);

        if (animate) {
            await this._animateCameraPropertiesChange(newAlpha, newBeta);
            await this._animateCameraFOVChange(fov);
            await new Promise(resolve => setTimeout(resolve, 200));
        }
        else {
            this.setCameraProperties(newAlpha, newBeta);
        }
    }

    
    public setCameraPosition(position: BABYLON.Vector3) {

        var dir2 = this.camera.getWorldDirection();
        var rad = this.camera.radius;        
        
        this.camera.target = position;
        this.camera.position = position.subtract(dir2.scale(rad));

        this.camera.rebuildAnglesAndRadius();
    }

    public setCameraProperties(alpha: number, beta: number, fov?: number) {
        this.camera.alpha = alpha;
        this.camera.beta = beta;
        if (fov) {
            this.camera.fov = fov;
        }
    }
    public setAssemblyCreationID(assemblyID: string) {

        if (this._assemblyManager != null)
            this._assemblyManager.setAssemblyCreationID(assemblyID);
    }
    public setAssemblyCreationTemplate(assemblyTemplate: IAssemblyTemplate) {

        if (this._assemblyManager != null)
            this._assemblyManager.setAssemblyCreationTemplate(assemblyTemplate);
    }
    
    public setMeasurementType(mode: MeasurementType) {
        if (this._measurementType == mode)
            return;

        this._measurementType = mode;

        if (this._measurementManager != null)
            this._measurementManager.setMeasurementMode(this._measurementType);
    }
    public setDisplayMode(mode: DisplayMode) {
        if (this._displayMode == mode) {
            return;
        }

        /*
        if (mode == DisplayMode.Measurement) {
            this._measurementManager.setMeasurementMode(this._measurementType);
        }
        else {
            this._measurementManager.setMeasurementMode(null);
        }
        */

        if (mode == DisplayMode.TagVolumeMove) {
            this._boxManager.setPositioningMode("move");
        }
        else if (mode == DisplayMode.TagVolumeScale) {
            this._boxManager.setPositioningMode("scale");
        }
        else {
            this._boxManager.setPositioningMode("none");
        }

        if (mode == DisplayMode.AssemblyVolumeMove) {
            this._assemblyManager.setSelectedAssemblyEdit();
        }
        else if (mode == DisplayMode.AssemblyVolumeScale) {
            this._assemblyManager.setSelectedAssemblyScale();
        }
        else {
            //this._assemblyManager.setPositioningMode("none");
        }

        if (mode == DisplayMode.CreateAnnotation || mode == DisplayMode.PositionAnnotation) {
            this._canvas.parentElement.style.cursor = 'crosshair';
        }
        else {
            this._canvas.parentElement.style.cursor = '';
         }

        this._displayMode = mode;
    }

    public setAllowZoom(allowZoom: boolean) {
        this._allowZoom = allowZoom;
    }
    private async _animateCameraPropertiesChange(newAlpha: number, newBeta: number) {
        const camera = this.camera;

        // Normalize new alpha
        newAlpha = newAlpha % (2 * Math.PI);
        if (newAlpha < 0) newAlpha += 2 * Math.PI;

        // Normalize current alpha
        let currentAlpha = camera.alpha % (2 * Math.PI);
        if (currentAlpha < 0) currentAlpha += 2 * Math.PI;

        // Determine the shortest path
        const deltaAlpha = newAlpha - currentAlpha;
        if (deltaAlpha > Math.PI) {
            newAlpha -= 2 * Math.PI;
        } else if (deltaAlpha < -Math.PI) {
            newAlpha += 2 * Math.PI;
        }

        // Create the animation object
        const alphaAnimation = new BABYLON.Animation("cameraAlphaAnimation", "alpha", 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
        alphaAnimation.setKeys([
            { frame: 0, value: currentAlpha },
            { frame: 60, value: newAlpha } // 1.0 seconds at 60 fps
        ]);

        // Create and set up the beta animation
        const betaAnimation = new BABYLON.Animation("cameraBetaAnimation", "beta", 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
        betaAnimation.setKeys([
            { frame: 0, value: camera.beta },
            { frame: 60, value: newBeta }
        ]);

        // Attach the animations to the camera
        camera.animations = [alphaAnimation, betaAnimation];

        // Start the animation
        await this._scene.beginAnimation(camera, 0, 60, false).waitAsync();
    }

    private async _animateCameraFOVChange(newFOV: number) {
        const camera = this.camera;

        // Create the animation object
        const fovAnimation = new BABYLON.Animation("cameraFOVAnimation", "fov", 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);

        // Set the keyframes for the animation
        const keys = [];
        keys.push({ frame: 0, value: camera.fov });
        keys.push({ frame: 60, value: newFOV }); // 1.0 seconds at 60 fps

        // Assign the keys to the animation
        fovAnimation.setKeys(keys);

        // Attach the animation to the camera
        camera.animations = [];
        camera.animations.push(fovAnimation);

        // Start the animation
        await this._scene.beginAnimation(camera, 0, 60, false, 1).waitAsync();
    }

    private _addEventListeners() {
        window.addEventListener('resize', this._resizeCanvas);
    }

    private _removeEventListeners() {
        window.removeEventListener('resize', this._resizeCanvas);
    }

    private _updateCompass = (camDegrees: { rotatedDegrees: number, upDegrees: number, upAxis: string }) => {
        this._compass._updateCompass({
            rotatedDegrees: camDegrees.rotatedDegrees,
            upDegrees: camDegrees.upDegrees + "° " + camDegrees.upAxis
        });
    }

    private _handleCameraChange = () => {
        const camDegrees = this.camera.getCameraDegrees();
        this._updateCompass(camDegrees);

        this._lastCameraDegrees = camDegrees.rotatedDegrees;
        this.events?.dispatchEvent(new Event(PointEvents.Viewer.VIEWER_DIRECTION_CHANGED));
    }

    private _resizeCanvas = () => {
        var width = this._canvas.parentElement.clientWidth;
        var height = this._canvas.parentElement.clientHeight;

        if ((this._canvas.width != width || this._canvas.height != height)) {

            this._canvas.style.width = width + 'px';
            this._canvas.style.height = height + 'px';

            //this is a hack to call the babylon engine to resize the 3d canvas.
            this._engine.setSize(width, height);

            this.events.dispatchEvent(new Event(PointEvents.Viewer.VIEWER_RESIZED));
        }
    }

    private _initializeBabylon(cameraPosition: any, imageDirection: any) {

        var canvas3dContainer = document.createElement('div');
        canvas3dContainer.className = 'vt-3d-container';
        canvas3dContainer.style.width = '100%';
        canvas3dContainer.style.height = '100%';
        canvas3dContainer.style.display = 'flex';
        canvas3dContainer.style.cursor = 'default';
        canvas3dContainer.style.justifyContent = 'center';

        // Set 3d canvas styles
        var canvas3d = this._canvas = document.createElement('canvas');
        canvas3d.style.width = '100%';
        canvas3d.style.height = '100%';
        canvas3d.style.padding = '0';
        canvas3d.style.margin = '0';
        canvas3d.style.backgroundColor = 'transparent';
        canvas3d.style.cursor = 'default';
        canvas3d.style.alignSelf = 'center';
        canvas3d.style.outline = '2px solid black';

        canvas3dContainer.appendChild(canvas3d);

        this._divContainer.appendChild(canvas3dContainer);

        this._addEventListeners();

        this._engine = new BABYLON.Engine(
            this._canvas,
            true
        );

        // There is a problem with drawing into the canvas (needed for scaling down large
        // image/texture sizes) in FireFox. The default canvas size for the scale-down operation
        // is 16384, which FireFox however cannot handle. Even 8192 causes occassional problems.
        // Using 6144 seems to be fine; if not, 4096 should definitely be safe.
        var isFirefox = typeof InstallTrigger !== 'undefined';
        if (isFirefox) {
            this._engine.getCaps().maxTextureSize = 6144;
        }
        
        this._canvas.addEventListener("contextmenu", function (e) {
            e.preventDefault();
        });

        this._scene = new BABYLON.Scene(this._engine);
        var camPos = new BABYLON.Vector3(cameraPosition.X, cameraPosition.Y, cameraPosition.Z);
        var camera = new PointCamera("Camera", 0, 0, .001, camPos, this._scene, Math.PI / 2);
        //camera.position = new BABYLON.Vector3(0, 0, 0);
        camera.getProjectionMatrix(true)
        camera.updateCache(true);
        //this.alignCamera(camPos.add(imageDirection));
        this.alignCamera(imageDirection);
        camera.attachControl(this._canvas, true);
        camera.minZ = .1;
        camera.maxZ = 1000;

        // Adjust the double click delay to 500ms (default is 300ms)
        BABYLON.Scene.DoubleClickDelay = 500;

        (camera as any).inputs.attached.pointers.buttons = [0, 1] as any;

        // Disable panning
        camera.panningSensibility = 0;

        // Detach default mousewheel handler, so that we can use our own mousewheel handling below
        camera.inputs.attached.mousewheel.detachControl();

        // Disable pinching
        (camera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
        (camera.inputs.attached.pointers as any).multiTouchPanning = false;
        camera.pinchToPanMaxDistance = 1e10;

        // Create an ambient light instance and set its intensity
        const ambientLight = new BABYLON.HemisphericLight("ambientLight", new BABYLON.Vector3(0, 1, 0), this._scene);
        ambientLight.intensity = 0.7; // Adjust the intensity as needed

        // Keep credentialed image loading for viewer media paths that are not fetch-based.
        BABYLON.Tools.CorsBehavior = "use-credentials";

        const events = this.events;

        var that = this;
        this._scene.onPointerObservable.add(function (e) {
            if (that._allowZoom) {
                let zoomLevel = camera.fov + (e.event as any).wheelDelta * -0.00025;

                if (zoomLevel < PointViewer.MinZoomLevel) {
                    zoomLevel = PointViewer.MinZoomLevel;
                }
                else if (zoomLevel > PointViewer.MaxZoomLevel) {
                    zoomLevel = PointViewer.MaxZoomLevel;
                }

                if (camera.fov != zoomLevel) {
                    camera.fov = zoomLevel;
                    events?.dispatchEvent(new Event(PointEvents.Viewer.VIEWER_FOV_CHANGED));
                }
            }
        }, BABYLON.PointerEventTypes.POINTERWHEEL);

        this._engine.runRenderLoop(() => {
            this._scene?.render();
        });
    }
}

export default PointViewer;